import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ReactiveFormsService } from '../../../shared/services/reactive-forms.service';
import { WorkflowsHelperService } from '../../services/workflows-helper.service';
import { MenuItemAction } from '../../../shared/models/master.models';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
	IStepProgress,
	ModalComponent,
	StepperMicroComponent,
	TileComponent,
	ItemComponent,
	ListDetailComponent,
	AlertComponent,
	EmptyStateComponent,
	FormGroupComponent,
	ToggleComponent,
	SwitchComponent,
	InputQuantityComponent,
	InputComponent,
	TextareaComponent,
	IToogleSelect,
	ITab,
	TabsComponent,
} from '@tecalisdev/infinity';
import { DEMAND_LEVEL, IDocInfo, IOperationInfo } from '../../models/workflows.models';
import { DOCS_TYPE } from '../../../operations/models/operations.models';
import { MasterService } from '../../../shared/services/master.service';
import { LanguageService } from '../../../core/services/language.service';
import { WorkflowFormDefaultsService } from '../../services/workflow-form-defaults.service';
import {
	firstMissingLocaleTextId,
	mergeGenericDocLocaleField,
	normalizeWorkflowLocaleText,
} from '../../utils/workflow-locale-text.util';

@Component({
	selector: 'app-modal-doc-form',
	imports: [
		ModalComponent,
		ReactiveFormsModule,
		StepperMicroComponent,
		TileComponent,
		ItemComponent,
		ListDetailComponent,
		AlertComponent,
		EmptyStateComponent,
		TranslatePipe,
		FormGroupComponent,
		ToggleComponent,
		SwitchComponent,
		InputQuantityComponent,
		InputComponent,
		TextareaComponent,
		TabsComponent,
	],
	templateUrl: './modal-doc-form.component.html',
	styleUrl: './modal-doc-form.component.scss',
})
export class ModalDocFormComponent implements OnInit {
	trans = inject(TranslateService);
	reactiveFormService = inject(ReactiveFormsService);
	workflowsHelper = inject(WorkflowsHelperService);
	private masterService = inject(MasterService);
	private languageService = inject(LanguageService);
	private workflowFormDefaults = inject(WorkflowFormDefaultsService);
	private cdr = inject(ChangeDetectorRef);

	@Input() info: { mode: MenuItemAction; prevSelectedDocs?: string[]; form?: any; index?: number };
	@Input() operationSelected: IOperationInfo[] = [];
	@Output() closeEvent = new EventEmitter<{ form: any; index?: number }>();

	public MenuItemAction = MenuItemAction;
	public form: FormGroup = this.workflowsHelper.formDoc;
	public docs: IDocInfo[] = this.availableDocs;
	public docsFiltered: IDocInfo[] = this.availableDocs;
	public steps: IStepProgress[] = [
		{ id: 'type', label: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.MODAL_DOC_FORM.TYPE'), selected: true },
		{ id: 'config', label: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.MODAL_DOC_FORM.CONFIG'), selected: false },
	];
	public methodOptions: IToogleSelect[] = [];
	public errorMsg: string = '';
	public docLocaleTabs: ITab[] = [];
	public selectedDocLocaleId: string = 'es';

	ngOnInit(): void {
		this.buildDocLocaleTabs();
		if (this.info.form) {
			const allowed = this.languageService.getAllowedLanguages();
			const raw = this.info.form;
			this.form.patchValue({
				...raw,
				title:
					raw.type === DOCS_TYPE.GENERIC
						? mergeGenericDocLocaleField(raw.title, allowed, this.workflowFormDefaults.getGenericDocTitleDefaults())
						: normalizeWorkflowLocaleText(raw.title, allowed),
				description:
					raw.type === DOCS_TYPE.GENERIC
						? mergeGenericDocLocaleField(
								raw.description,
								allowed,
								this.workflowFormDefaults.getGenericDocDescriptionDefaults(),
							)
						: normalizeWorkflowLocaleText(raw.description, allowed),
				instructions: normalizeWorkflowLocaleText(raw.instructions, allowed),
				instructions_photo: normalizeWorkflowLocaleText(raw.instructions_photo, allowed),
			});
			this.configStepInstructions(this.form.get('type').value);
			this.setDemandLevel();
		}
		this.syncInstructionsValidators();
		if (this.info?.prevSelectedDocs?.length) {
			this.docsFiltered = this.docs.filter((doc: IDocInfo) => !this.info.prevSelectedDocs.includes(doc.type));
		}
	}

	public closeModal(type: string) {
		const actions = {
			buttonPrimary: () => {
				this.errorMsg = '';
				// Primer paso
				if (this.steps.at(0).selected) {
					if (this.form.get('type')?.valid) {
						this.steps.at(0).selected = false;
						this.steps.at(1).selected = true;
						this.updateMethodOptions();
					} else {
						this.errorMsg = this.trans.instant('WORKFLOWS.FORM.OPERATIONS.MODAL_DOC_FORM.DOC_REQUIRED');
					}
					return;
				}
				// Paso de configuración en modo genérico
				if (this.form.get('type').value === DOCS_TYPE.GENERIC && this.steps.at(1).selected) {
					this.steps.forEach((item) => {
						item.selected = false;
					});
					this.steps[this.steps.length - 1].selected = true;
					return;
				}
				// Último paso y el formulario es valido
				if (this.steps[this.steps.length - 1].selected) {
					if (this.form.invalid) {
						this.form.markAllAsTouched();
						this.focusDocLocaleOnInvalid();
						return;
					}
					const demandLevel = this.form.get('demand_level').value?.id ?? this.form.get('demand_level').value;
					// NIF-993: en no-GENERIC el toggle RECOMMENDED/CUSTOM está oculto y el default
					// es RECOMMENDED → no pisar total_tries editado con confRecommended (p. ej. 3).
					const isGeneric = this.form.get('type').value === DOCS_TYPE.GENERIC;
					if (isGeneric && demandLevel === DEMAND_LEVEL.RECOMMENDED) {
						this.form
							.get('total_tries')
							.patchValue(this.form.get('docInfo').value.confRecommended.total_tries);
						this.form.get('min').patchValue(this.form.get('docInfo').value.confRecommended.min);
						this.form.get('max').patchValue(this.form.get('docInfo').value.confRecommended.max);
						this.form
							.get('preview_validation')
							.patchValue(this.form.get('docInfo').value.confRecommended.preview_validation);
						this.form
							.get('match_data')
							.patchValue(this.form.get('docInfo').value.confRecommended.match_data);
					}
					this.closeEvent.emit({ form: this.form.getRawValue(), index: this.info.index });
				}
			},
		};

		actions[type] ? actions[type]() : this.closeEvent.emit(null);
	}

	public selectDoc(doc: IDocInfo): void {
		const allowed = this.languageService.getAllowedLanguages();
		this.configStepInstructions(doc.type);
		this.form.get('type').patchValue(doc.type);
		this.form.get('docInfo').patchValue(doc);
		this.form.get('total_tries').patchValue(doc.confRecommended.total_tries);
		this.form.get('min').patchValue(doc.confRecommended.min);
		this.form.get('max').patchValue(doc.confRecommended.max);
		this.form.get('preview_validation').patchValue(doc.confRecommended.preview_validation);
		this.form.get('match_data').patchValue(doc.confRecommended.match_data);
		if (doc.type === DOCS_TYPE.GENERIC) {
			this.form
				.get('title')
				.patchValue(mergeGenericDocLocaleField(null, allowed, this.workflowFormDefaults.getGenericDocTitleDefaults()));
			this.form
				.get('description')
				.patchValue(
					mergeGenericDocLocaleField(null, allowed, this.workflowFormDefaults.getGenericDocDescriptionDefaults()),
				);
			this.form.get('instructions').patchValue(normalizeWorkflowLocaleText(null, allowed));
		} else {
			this.form.get('title').patchValue(normalizeWorkflowLocaleText(doc.title, allowed));
			this.form.get('description').patchValue(normalizeWorkflowLocaleText(doc.description, allowed));
		}
		this.form.get('icon').patchValue(doc.icon);
		this.syncInstructionsValidators();
	}

	/** GENÉRICO: pautas obligatorias en todos los idiomas; otros tipos: regla opcional / todos si hay algo escrito. */
	private syncInstructionsValidators(): void {
		const docType = this.form.get('type')?.value ?? '';
		this.workflowsHelper.applyDocInstructionsValidatorsForType(
			this.form.get('instructions') as FormGroup,
			docType,
		);
	}

	private buildDocLocaleTabs(): void {
		const langs = this.languageService.getAllowedLanguages();
		this.selectedDocLocaleId = langs[0] ?? 'es';
		this.docLocaleTabs = langs.map((lang) => ({
			id: lang,
			label: this.trans.instant(`MASTER.LANGUAGE.${lang.toUpperCase()}`),
			selected: lang === this.selectedDocLocaleId,
		}));
	}

	public onDocLocaleTabSelected(tab: { id: string; label: string }): void {
		const id = tab?.id != null ? String(tab.id) : '';
		if (!id) {
			return;
		}
		this.selectedDocLocaleId = id;
		this.docLocaleTabs = this.docLocaleTabs.map((t) => ({
			...t,
			selected: String(t.id) === id,
		}));
	}

	/** Control del locale activo (evita [formControlName] dinámico). */
	public docLocaleControl(...segments: string[]): FormControl {
		let c: AbstractControl | null = this.form;
		for (const s of segments) {
			c = c?.get(s) ?? null;
		}
		return c as FormControl;
	}

	/** Pestaña y scroll al primer campo multi-idioma inválido; foco en el control concreto que falla. */
	private focusDocLocaleOnInvalid(): void {
		const allowed = this.languageService.getAllowedLanguages();
		for (const field of ['title', 'description', 'instructions'] as const) {
			const g = this.form.get(field) as FormGroup | null;
			if (g?.invalid) {
				const id = firstMissingLocaleTextId(g, allowed);
				if (id) {
					this.selectedDocLocaleId = id;
					this.docLocaleTabs = this.docLocaleTabs.map((t) => ({
						...t,
						selected: String(t.id) === id,
					}));
					this.cdr.detectChanges();
					const anchorId = `modal-doc-locale-field-${field}`;
					setTimeout(() => {
						document.getElementById(anchorId)?.scrollIntoView({
							behavior: 'smooth',
							block: 'center',
							inline: 'nearest',
						});
						const host = document.getElementById(anchorId);
						const focusable = host?.querySelector('input, textarea') as HTMLElement | null;
						focusable?.focus?.();
					}, 320);
					return;
				}
			}
		}
	}

	private setDemandLevel(): void {
		if (this.form.get('type').value !== DOCS_TYPE.GENERIC) {
			const configDoc = this.workflowsHelper.docsInfo.find(
				(doc) => doc.type === this.form.get('type').value,
			)?.confRecommended;
			if (configDoc) {
				this.form
					.get('demand_level')
					.patchValue(
						+configDoc.total_tries === +this.form.get('total_tries').value &&
							+configDoc.min === +this.form.get('min').value &&
							+configDoc.max === +this.form.get('max').value &&
							Boolean(configDoc.preview_validation) ===
								Boolean(this.form.get('preview_validation').value) &&
							Boolean(configDoc.match_data) === Boolean(this.form.get('match_data').value)
							? DEMAND_LEVEL.RECOMMENDED
							: DEMAND_LEVEL.CUSTOM,
					);
			}
		}
	}

	private configStepInstructions(type: string): void {
		if (type === DOCS_TYPE.GENERIC) {
			this.form.get('demand_level').patchValue(DEMAND_LEVEL.CUSTOM);
			this.steps.push({
				id: 'instructions',
				label: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.MODAL_DOC_FORM.INSTRUCTIONS'),
				selected: false,
			});
		} else {
			const found = this.steps.findIndex((step) => step.id === 'instructions');
			if (found !== -1) {
				this.steps.splice(found, 1);
			}
		}
	}

	public clickStep(step: any): void {
		if (step.id === 'config' && !this.form.get('type')?.valid) {
			this.errorMsg = this.trans.instant('WORKFLOWS.FORM.OPERATIONS.MODAL_DOC_FORM.DOC_REQUIRED');
			return;
		}
		this.steps.forEach((item) => {
			item.selected = false;
		});
		step.selected = true;
	}

	get title(): string {
		if (this.info) {
			if (this.info.mode === MenuItemAction.CREATE) {
				return this.trans.instant('WORKFLOWS.FORM.OPERATIONS.MODAL_DOC_FORM.CREATE_TITLE');
			}
			if (this.info.mode === MenuItemAction.EDIT) {
				return this.trans.instant('WORKFLOWS.FORM.OPERATIONS.MODAL_DOC_FORM.EDIT_TITLE');
			}
		}
		return '';
	}

	get buttonPrimaryLabel(): string {
		return this.steps[this.steps.length - 1].selected
			? this.trans.instant('BUTTON.SAVE')
			: this.trans.instant('BUTTON.CONTINUE');
	}

	get availableDocs(): IDocInfo[] {
		return this.workflowsHelper.docsInfo?.filter(
			(doc: IDocInfo) =>
				doc.type !== DOCS_TYPE.DNI &&
				doc.type !== DOCS_TYPE.PASSPORT &&
				doc.type !== DOCS_TYPE.NIE &&
				doc.type !== DOCS_TYPE.SCRIPTURE,
		);
	}

	private updateMethodOptions(): void {
		const docType = this.form.get('type')?.value;

		const methodOptions = [
			{
				id: 'manual',
				label: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.MODAL_DOC_FORM.ATTACH'),
				tooltip: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.MODAL_DOC_FORM.ATTACH_TOOLTIP'),
			},
		];

		const docInfo = this.masterService.master()?.document_types?.find((doc) => doc.name === docType);
		if (docInfo?.allow_photo) {
			methodOptions.push({
				id: 'photo',
				label: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.MODAL_DOC_FORM.PHOTO'),
				tooltip: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.MODAL_DOC_FORM.PHOTO_TOOLTIP'),
			});
		} else {
			const currentMethod = this.form.get('method').value;
			const photoId = 'photo';
			if (currentMethod.find((opt: IToogleSelect) => opt.id === photoId)) {
				const method = currentMethod.filter((method: IToogleSelect) => method.id !== photoId);
				this.form.get('method').patchValue(method);
			}
		}

		this.methodOptions = methodOptions;
	}
}
